import type { TranscriptLine } from "@scoach/types";
import { Check, Copy, Notebook, Search, Trash } from "@scoach/ui/icons";
import { collection, deleteDoc, doc, getDocs, orderBy, query } from "firebase/firestore";
import { useEffect, useState } from "react";

import { getDb } from "../../../lib/firestore.ts";
import { summaryApi } from "../api.ts";

export interface FullTranscriptProps {
  meetingId: string;
}

export function FullTranscript({ meetingId }: FullTranscriptProps) {
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Load via the API first — this works even when a client ad-blocker blocks
      // direct Firestore channels. Fall back to a direct Firestore read.
      try {
        const apiLines = await summaryApi.fetchTranscript(meetingId);
        if (cancelled) return;
        if (apiLines.length > 0) {
          setLines(apiLines);
          return;
        }
      } catch (err) {
        console.warn("[transcript] API load failed, trying Firestore", err);
      }
      try {
        const db = getDb();
        const ref = doc(db, "meetings", meetingId);
        const q = query(collection(ref, "transcript"), orderBy("_at", "asc"));
        const snap = await getDocs(q);
        if (cancelled) return;
        setLines(snap.docs.map((d) => d.data() as TranscriptLine));
      } catch (err) {
        console.warn("[transcript] failed to load", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  async function handleDeleteTranscript() {
    if (!window.confirm("Delete the entire transcript for this meeting? This cannot be undone."))
      return;
    setDeleting(true);
    try {
      const db = getDb();
      const ref = doc(db, "meetings", meetingId);
      const snap = await getDocs(collection(ref, "transcript"));
      const batch: Promise<void>[] = [];
      for (const d of snap.docs) {
        batch.push(deleteDoc(d.ref));
      }
      await Promise.all(batch);
      setLines([]);
    } catch (err) {
      console.warn("[transcript] delete failed", err);
    } finally {
      setDeleting(false);
    }
  }

  const filtered = lines.filter((l) => {
    if (
      search &&
      !l.text.toLowerCase().includes(search.toLowerCase()) &&
      !l.name.toLowerCase().includes(search.toLowerCase())
    )
      return false;
    return true;
  });

  // Copy / export operate on the currently-visible lines (respecting the filter/search).
  const asText = (): string => filtered.map((l) => `[${l.t}] ${l.name}: ${l.text}`).join("\n");

  async function copyTranscript() {
    try {
      await navigator.clipboard.writeText(asText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — ignore */
    }
  }

  function downloadTxt() {
    const blob = new Blob([asText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transcript-${meetingId.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    setExportingPdf(true);
    try {
      const blob = await summaryApi.exportTranscriptPdf(meetingId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transcript-${meetingId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("[transcript] pdf export failed", err);
    } finally {
      setExportingPdf(false);
    }
  }

  return (
    <div className="ft-wrap">
      <div className="ft-head">
        <div className="search-box">
          <Search size={14} />
          <input
            type="search"
            placeholder="Search transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {lines.length > 0 && (
          <>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => void copyTranscript()}
              style={{ fontSize: 12, gap: 4 }}
              title="Copy transcript to clipboard"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={downloadTxt}
              style={{ fontSize: 12, gap: 4 }}
              title="Download as .txt"
            >
              <Notebook size={13} /> .txt
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => void downloadPdf()}
              disabled={exportingPdf}
              style={{ fontSize: 12, gap: 4 }}
              title="Download as PDF"
            >
              <Notebook size={13} /> {exportingPdf ? "…" : ".pdf"}
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={handleDeleteTranscript}
              disabled={deleting}
              style={{ color: "var(--gc-red, #d93025)", fontSize: 12, gap: 4 }}
            >
              <Trash size={13} /> {deleting ? "Deleting…" : "Delete transcript"}
            </button>
          </>
        )}
      </div>
      <div className="ft-body">
        {loading && (
          <div
            className="ft-line"
            style={{ color: "var(--text-3)", textAlign: "center", padding: "32px 16px" }}
          >
            Loading transcript…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div
            className="ft-line"
            style={{ color: "var(--text-3)", textAlign: "center", padding: "32px 16px" }}
          >
            {lines.length === 0
              ? "No transcript recorded for this meeting."
              : "No lines match your filter."}
          </div>
        )}
        {filtered.map((line) => (
          <div key={line.id} className="ft-line">
            <span className="ft-time mono">{line.t}</span>
            <span className={`ft-speaker ${line.speaker === "rep" ? "ft-rep" : "ft-client"}`}>
              {line.name}
            </span>
            <span className="ft-text" dir="auto">
              {line.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
